import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { ExternalLink, Loader2, FileSignature, RefreshCw } from "lucide-react";
import ESignStatusBadge from "./ESignStatusBadge";
import { useUser } from "../context/UserContext";
import { listAgreementRequests, requestSigningUrl } from "../services/esign";

const SIGNED_SIGNER = new Set(["SIGNED", "DECLINED", "EXPIRED"]);
const TERMINAL = new Set(["COMPLETED", "EXPIRED", "DECLINED", "CANCELLED", "FAILED"]);

// Which agreement signer role the logged-in persona may sign here.
const personaSignRole = (persona) => {
  if (persona === "ZUMALE_USER") return "BUILDVIZER";
  if (persona === "DEVELOPER_ADMIN" || persona === "DEVELOPER") return "DEVELOPER";
  return null;
};

const prettyRole = (r) => r.replace(/_/g, " ").toLowerCase();

/**
 * Shared list of Interior Design & Execution Agreement signature requests, split into Pending and
 * Completed. Reused by the zumale-admin "Requests" tab (signs the BuildVizer party) and the
 * developer "Requests" tab (signs the Developer party). The backend scopes the list by caller.
 */
const AgreementRequestsList = () => {
  const { userInfo } = useUser();
  const signRole = personaSignRole(userInfo?.persona);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listAgreementRequests();
      setRows(Array.isArray(res) ? res : res?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load agreements");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const signAs = async (requestId, role) => {
    try {
      const { url } = await requestSigningUrl(requestId, role);
      window.open(url, "_blank", "noopener");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open the signing page");
    }
  };

  const pending = rows.filter((r) => r.status !== "COMPLETED");
  const completed = rows.filter((r) => r.status === "COMPLETED");

  const renderRow = (r) => {
    const mySigner = signRole ? r.signers?.find((s) => s.role === signRole) : null;
    const canSign =
      mySigner && !SIGNED_SIGNER.has(mySigner.status) && !TERMINAL.has(r.status);
    return (
      <div key={r.esignSignatureRequestId} className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-slate-800">
              {r.projectName || "—"}
              {r.phaseName ? <span className="text-slate-400"> · {r.phaseName}</span> : null}
            </p>
            <p className="text-xs text-slate-500">
              Unit {r.unitNo || "—"} · {r.agreementNo} · {r.buyerNames || "—"}
            </p>
          </div>
          <ESignStatusBadge status={r.status} />
        </div>

        <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
          {(r.signers || []).map((s) => (
            <li key={s.esignSignerId} className="text-xs text-slate-600">
              <span className="capitalize">{prettyRole(s.role)}</span>:{" "}
              <span className={s.status === "SIGNED" ? "font-semibold text-green-700" : "text-amber-700"}>
                {s.status.toLowerCase()}
              </span>
            </li>
          ))}
        </ul>

        <div className="mt-3 flex items-center gap-3">
          {canSign && (
            <button
              onClick={() => signAs(r.esignSignatureRequestId, mySigner.role)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-flamingo-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-flamingo-600"
            >
              <FileSignature size={13} /> Sign via Aadhaar
            </button>
          )}
          {r.status === "COMPLETED" && r.signedPdfUrl && (
            <a
              href={r.signedPdfUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs font-medium text-flamingo-600 hover:underline"
            >
              <ExternalLink size={13} /> View signed agreement
            </a>
          )}
        </div>
      </div>
    );
  };

  const Section = ({ title, items, empty }) => (
    <div className="mb-6">
      <div className="mb-2 flex items-center gap-2">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">{title}</h3>
        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">{items.length}</span>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-slate-400">{empty}</p>
      ) : (
        <div className="space-y-2">{items.map(renderRow)}</div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-6 text-sm text-slate-500">
        <Loader2 size={16} className="animate-spin" /> Loading agreements…
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4 flex justify-end">
        <button
          onClick={load}
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
        >
          <RefreshCw size={13} /> Refresh
        </button>
      </div>
      <Section title="Pending" items={pending} empty="No agreements awaiting signature." />
      <Section title="Completed" items={completed} empty="No fully-signed agreements yet." />
    </div>
  );
};

export default AgreementRequestsList;
